import { EAnalyticsEvent, isAnalyticsEventName } from './event-names';

/**
 * Per-event `params` (каталог §2) + декларативный валидатор. Единый источник правды
 * для клиентской разметки и ingest-валидации. Зависимостей нет (переиспользуется в
 * Angular и NestJS, tree-shakeable). Ключи `params` — snake_case: это формат провода
 * (JSONB в raw-партициях + совпадение с GA4/Метрикой), не внутренняя модель. PII в
 * params не допускается по дизайну.
 */

/** Оценка Core Web Vital (`web_vital.rating`). */
export enum EWebVitalRating {
    Good = 'good',
    NeedsImprovement = 'needs-improvement',
    Poor = 'poor',
}

const WEB_VITAL_RATINGS: readonly EWebVitalRating[] = Object.values(EWebVitalRating);

// snake_case ключи params — это wire-контракт (JSONB в raw-партициях + совпадение с
// GA4/Метрикой param names), а не внутренняя модель; camelCase-конвенция тут неуместна.
export namespace IAnalyticsParams {
    export interface SessionStart {
        landing_path?: string;
        referrer?: string;
        utm_source?: string;
        utm_medium?: string;
        utm_campaign?: string;
    }
    export interface PageView {
        page_path: string;
        page_title?: string;
    }
    export interface ParamChange {
        field: string;
        value: string | number | boolean;
    }
    export interface Result {
        step_count: number;
        total_run: number;
        value: number;
        currency: string;
    }
    export interface Placement {
        placement: string;
    }
    export interface FormOpen {
        form_type: string;
    }
    export interface FormSuccess {
        form_type: string;
        value?: number;
    }
    export interface FormError {
        form_type: string;
        error_code: string;
    }
    export interface Phone {
        phone_mask: string;
    }
    export interface Pdf {
        file_type: string;
        stair_type: string;
    }
    export interface Share {
        channel: string;
    }
    export interface Lead {
        value: number;
        currency: string;
        lead_id: string;
    }
    export interface WebVital {
        metric: string;
        value: number;
        rating: EWebVitalRating;
    }
    export interface ErrorInfo {
        error_type: string;
        message_short: string;
    }
}

/** Валидное событие каталога: имя из {@link EAnalyticsEvent} + типизированный `params`. */
export type IAnalyticsEvent =
    | { name: EAnalyticsEvent.SessionStart; params?: IAnalyticsParams.SessionStart }
    | { name: EAnalyticsEvent.PageView; params: IAnalyticsParams.PageView }
    | { name: EAnalyticsEvent.CalculatorOpen }
    | { name: EAnalyticsEvent.CalculatorParamChange; params: IAnalyticsParams.ParamChange }
    | { name: EAnalyticsEvent.CalculatorResult; params: IAnalyticsParams.Result }
    | { name: EAnalyticsEvent.CtaOrderClick; params: IAnalyticsParams.Placement }
    | { name: EAnalyticsEvent.CtaContactClick; params: IAnalyticsParams.Placement }
    | { name: EAnalyticsEvent.FormOpen; params: IAnalyticsParams.FormOpen }
    | { name: EAnalyticsEvent.FormSubmitSuccess; params: IAnalyticsParams.FormSuccess }
    | { name: EAnalyticsEvent.FormSubmitError; params: IAnalyticsParams.FormError }
    | { name: EAnalyticsEvent.PhoneClick; params: IAnalyticsParams.Phone }
    | { name: EAnalyticsEvent.MessengerWhatsappClick }
    | { name: EAnalyticsEvent.MessengerTelegramClick }
    | { name: EAnalyticsEvent.MessengerViberClick }
    | { name: EAnalyticsEvent.PdfDownload; params: IAnalyticsParams.Pdf }
    | { name: EAnalyticsEvent.ShareClick; params: IAnalyticsParams.Share }
    | { name: EAnalyticsEvent.LeadSubmit; params: IAnalyticsParams.Lead }
    | { name: EAnalyticsEvent.WebVital; params: IAnalyticsParams.WebVital }
    | { name: EAnalyticsEvent.Error; params: IAnalyticsParams.ErrorInfo };

/** Результат валидации: либо распарсенное событие, либо человекочитаемые проблемы. */
export type IAnalyticsValidationResult =
    { readonly ok: true; readonly event: IAnalyticsEvent } | { readonly ok: false; readonly issues: readonly string[] };

/** Тип поля в правиле валидации `params`. */
enum EFieldType {
    String = 'строка',
    Number = 'число',
    Bool = 'булево',
    Scalar = 'строка|число|булево',
    Rating = 'web-vital rating',
}

interface IFieldRule {
    readonly key: string;
    readonly type: EFieldType;
    readonly required: boolean;
}

function rule(key: string, type: EFieldType, required: boolean): IFieldRule {
    return { key, type, required };
}

/**
 * Правила `params` на каждое событие (данные, не императивный код) — ключи-провода
 * фигурируют ровно один раз. Пустой список = событие без params.
 */
const PARAM_RULES: Record<EAnalyticsEvent, readonly IFieldRule[]> = {
    [EAnalyticsEvent.SessionStart]: [
        rule('landing_path', EFieldType.String, false),
        rule('referrer', EFieldType.String, false),
        rule('utm_source', EFieldType.String, false),
        rule('utm_medium', EFieldType.String, false),
        rule('utm_campaign', EFieldType.String, false),
    ],
    [EAnalyticsEvent.PageView]: [rule('page_path', EFieldType.String, true), rule('page_title', EFieldType.String, false)],
    [EAnalyticsEvent.CalculatorOpen]: [],
    [EAnalyticsEvent.CalculatorParamChange]: [rule('field', EFieldType.String, true), rule('value', EFieldType.Scalar, true)],
    [EAnalyticsEvent.CalculatorResult]: [
        rule('step_count', EFieldType.Number, true),
        rule('total_run', EFieldType.Number, true),
        rule('value', EFieldType.Number, true),
        rule('currency', EFieldType.String, true),
    ],
    [EAnalyticsEvent.CtaOrderClick]: [rule('placement', EFieldType.String, true)],
    [EAnalyticsEvent.CtaContactClick]: [rule('placement', EFieldType.String, true)],
    [EAnalyticsEvent.FormOpen]: [rule('form_type', EFieldType.String, true)],
    [EAnalyticsEvent.FormSubmitSuccess]: [rule('form_type', EFieldType.String, true), rule('value', EFieldType.Number, false)],
    [EAnalyticsEvent.FormSubmitError]: [rule('form_type', EFieldType.String, true), rule('error_code', EFieldType.String, true)],
    [EAnalyticsEvent.PhoneClick]: [rule('phone_mask', EFieldType.String, true)],
    [EAnalyticsEvent.MessengerWhatsappClick]: [],
    [EAnalyticsEvent.MessengerTelegramClick]: [],
    [EAnalyticsEvent.MessengerViberClick]: [],
    [EAnalyticsEvent.PdfDownload]: [rule('file_type', EFieldType.String, true), rule('stair_type', EFieldType.String, true)],
    [EAnalyticsEvent.ShareClick]: [rule('channel', EFieldType.String, true)],
    [EAnalyticsEvent.LeadSubmit]: [
        rule('value', EFieldType.Number, true),
        rule('currency', EFieldType.String, true),
        rule('lead_id', EFieldType.String, true),
    ],
    [EAnalyticsEvent.WebVital]: [
        rule('metric', EFieldType.String, true),
        rule('value', EFieldType.Number, true),
        rule('rating', EFieldType.Rating, true),
    ],
    [EAnalyticsEvent.Error]: [rule('error_type', EFieldType.String, true), rule('message_short', EFieldType.String, true)],
};

function isString(value: unknown): boolean {
    return typeof value === 'string';
}

function isFiniteNumber(value: unknown): boolean {
    return typeof value === 'number' && Number.isFinite(value);
}

function isBool(value: unknown): boolean {
    return typeof value === 'boolean';
}

function isScalar(value: unknown): boolean {
    return isString(value) || isFiniteNumber(value) || isBool(value);
}

function isRating(value: unknown): boolean {
    return typeof value === 'string' && (WEB_VITAL_RATINGS as readonly string[]).includes(value);
}

const TYPE_PREDICATES: Record<EFieldType, (value: unknown) => boolean> = {
    [EFieldType.String]: isString,
    [EFieldType.Number]: isFiniteNumber,
    [EFieldType.Bool]: isBool,
    [EFieldType.Scalar]: isScalar,
    [EFieldType.Rating]: isRating,
};

function matchesType(value: unknown, type: EFieldType): boolean {
    return TYPE_PREDICATES[type](value);
}

function checkField(params: Record<string, unknown>, fieldRule: IFieldRule, issues: string[]): void {
    const value: unknown = params[fieldRule.key];
    if (value === undefined) {
        if (fieldRule.required) {
            issues.push(`params.${fieldRule.key}: обязательное поле отсутствует`);
        }
        return;
    }
    if (!matchesType(value, fieldRule.type)) {
        issues.push(`params.${fieldRule.key}: ожидается ${fieldRule.type}`);
    }
}

function asRecord(value: unknown): Record<string, unknown> {
    return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
}

/**
 * Проверяет произвольный вход против каталога: имя известно и `params` удовлетворяют
 * правилам события. Переиспользуется клиентским SDK (перед отправкой) и
 * ingest-эндпоинтом (перед записью в raw-партиции).
 */
export function validateAnalyticsEvent(input: unknown): IAnalyticsValidationResult {
    if (typeof input !== 'object' || input === null) {
        return { ok: false, issues: ['(root): ожидается объект события'] };
    }
    const record: { name?: unknown; params?: unknown } = input;
    const name: unknown = record.name;
    if (typeof name !== 'string' || !isAnalyticsEventName(name)) {
        return { ok: false, issues: [`name: неизвестное событие "${String(name)}"`] };
    }

    const params: Record<string, unknown> = asRecord(record.params);
    const issues: string[] = [];
    for (const fieldRule of PARAM_RULES[name]) {
        checkField(params, fieldRule, issues);
    }
    if (issues.length > 0) {
        return { ok: false, issues };
    }
    return { ok: true, event: record as IAnalyticsEvent };
}
